import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { parseServerError } from "utils/errors";

export const fetchPaymentInfo = createAsyncThunk(
  "profile/fetchPaymentInfo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/dashboard/payment_info", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);
export const fetchCompanyApp = createAsyncThunk(
  "profile/fetchCompanyApp",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/company/app", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const editCompanyApp = createAsyncThunk(
    "auth/editCompanyApp",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/company/app", "POST", data);
        if (!response.ok) {
          throw new Error("Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
);

export const fetchDomainData = createAsyncThunk(
    "profile/fetchDomainData",
    async (arg, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/domains_data", "GET");
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.json();
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const addDomain = createAsyncThunk(
    "auth/addDomain",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/add_domain", "POST", data);
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData.error || "Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
);
export const verificationDomain = createAsyncThunk(
    "auth/verificationDomain",
    async (domain, { rejectWithValue }) => {
      try {
        const response = await callApi(`/sso/domain_add_data/${domain}`, "GET");
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData.error || "Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
);

// New SSO API functions
export const setupSsoConfiguration = createAsyncThunk(
    "profile/setupSsoConfiguration",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/setup_sso_configuration", "POST", data);
        
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData.error || "Server error");
        }
        
        const responseData = await response.json();
        return responseData;
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
  );

export const addTestEmail = createAsyncThunk(
    "profile/addTestEmail",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/add_test_email", "POST", data);
        
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData.error || "Server error");
        }
        
        const responseData = await response.json();
        return responseData;
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
  );

export const getTestAuthUrl = createAsyncThunk(
    "profile/getTestAuthUrl",
    async (_, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/get_test_auth_url", "GET");
        if (!response.ok) {
          throw new Error("Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const getSsoConfStatus = createAsyncThunk(
    "profile/getSsoConfStatus",
    async (ssoConfId, { rejectWithValue }) => {
      // console.info("[getSsoConfStatus thunk body]", { timestamp: Date.now(), ssoConfId, stack: new Error().stack });
      try {
        const response = await callApi(`/sso/sso_conf_status/${ssoConfId}`, "GET");
        if (!response.ok) {
          throw new Error("Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const getSsoSettingsToAdd = createAsyncThunk(
    "profile/getSsoSettingsToAdd",
    async (_, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/sso_settings_to_add", "GET");
        if (!response.ok) {
          throw new Error("Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const deactivateSso = createAsyncThunk(
    "profile/deactivateSso",
    async (ssoConfId, { rejectWithValue }) => {
      try {
        const response = await callApi(`/sso/deactivate_sso/${ssoConfId}`, "POST");
        if (!response.ok) {
          try {
            const errorData = await response.json();
            return rejectWithValue(errorData.error || errorData.detail || "Server error");
          } catch {
            return rejectWithValue("Server error");
          }
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message || "Server error");
      }
    }
);

export const activateSso = createAsyncThunk(
    "profile/activateSso",
    async (ssoConfId, { rejectWithValue }) => {
      try {
        const response = await callApi(`/sso/activate_sso/${ssoConfId}`, "POST");
        if (!response.ok) {
          // Try to get error message from response
          try {
            const errorData = await response.json();
            return rejectWithValue(errorData.error || "Server error");
          } catch (parseError) {
            return rejectWithValue("Server error");
          }
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
);


export const getAuthUrl = createAsyncThunk(
    "profile/getAuthUrl",
    async (_, { rejectWithValue }) => {
      try {
        const response = await callApi("/sso/get_auth_url", "GET");
        if (!response.ok) {
          throw new Error("Server error");
        }
        return response.json();
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);



export const deleteSso = createAsyncThunk(
    "profile/deleteSso",
    async (ssoConfId, { rejectWithValue }) => {
      try {
        const response = await callApi(`/sso/delete_sso/${ssoConfId}`, "DELETE");
        if (!response.ok) {
          // Try to get error message from response
          try {
            const errorData = await response.json();
            return rejectWithValue(errorData.error || "Server error");
          } catch (parseError) {
            return rejectWithValue("Server error");
          }
        }
        return response.json();
      } catch (err) {
        return rejectWithValue(err.message);
      }
    }
);

const initialState = {
  paymentInfo: [],
  fetchingPaymentInfo: false,
  fetchingPaymentInfoError: null,
  companyApp: [],
  domainData: [],
  domainAddData: {},
  // New SSO states
  ssoConfiguration: null,
  testAuthUrl: null,
  ssoConfStatus: null,
  ssoSettings: null,
  ssoSettingsToAdd: null,
  fetchingSsoSettingsToAdd: false,
  fetchingSsoSettingsToAddError: null,
  authUrl: null,
  loginUrl: null,
  // SSO deactivation states
  deactivatingSso: false,
  deactivatingSsoError: null,
  // SSO activation states
  activatingSso: false,
  activatingSsoError: null,
  // SSO deletion states
  deletingSso: false,
  deletingSsoError: null,
  // SSO form data persistence
  ssoFormData: {
    domain: '',
    domain_id: null,
    sso_conf_id: null,
    idpUrl: '',
    issuerId: '',
    certificate: '',
    email: ''
  }
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  	reducers: {
		clearFetchingAddDomainError: (state) => {
			state.fetchingAddDomainError = null;
		},
		clearFetchingVerificationDomainError: (state) => {
			state.fetchingvVrificationDomainError = null;
		},
		clearFetchingSetupSsoError: (state) => {
			state.fetchingSetupSsoError = null;
		},
		clearFetchingAddTestEmailError: (state) => {
			state.fetchingAddTestEmailError = null;
		},
		clearLoginUrl: (state) => {
			state.loginUrl = null;
		},
		/** Client-side FAILED for add-domain flow (e.g. polling timeout) — same shape as API failure for UI. */
		setSsoConfStatusClientFailure: (state, action) => {
			state.ssoConfStatus = {
				sso_conf_status: "FAILED",
				error_message: action.payload?.error_message || "",
				failed_step: action.payload?.failed_step ?? null,
			};
			state.fetchingSsoConfStatus = false;
			state.fetchingSsoConfStatusError = null;
		},
		clearSsoConfStatus: (state) => {
			state.ssoConfStatus = null;
			state.fetchingSsoConfStatus = false;
			state.fetchingSsoConfStatusError = null;
		},
		updateSsoFormData: (state, action) => {
			state.ssoFormData = { ...state.ssoFormData, ...action.payload };
		},
		clearSsoFormData: (state) => {
			state.ssoFormData = {
				domain: '',
				domain_id: null,
				sso_conf_id: null,
				idpUrl: '',
				issuerId: '',
				certificate: '',
				email: ''
			};
		}
	},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentInfo.pending, (state) => {
        state.fetchingPaymentInfo = true;
      })
      .addCase(fetchPaymentInfo.fulfilled, (state, action) => {
        state.fetchingPaymentInfo = false;
        state.fetchingPaymentInfoError = null;
        state.paymentInfo = action.payload;
      })
      .addCase(fetchPaymentInfo.rejected, (state, action) => {
        state.fetchingPaymentInfo = false;
        state.fetchingPaymentInfoError = parseServerError(action.error);
      })
      .addCase(fetchCompanyApp.pending, (state) => {
        state.fetchingCompanyApp = true;
      })
      .addCase(fetchCompanyApp.fulfilled, (state, action) => {
        state.fetchingCompanyApp = false;
        state.fetchingCompanyAppError = null;
        state.companyApp = action.payload;
      })
      .addCase(fetchCompanyApp.rejected, (state, action) => {
        state.fetchingCompanyApp = false;
        state.fetchingCompanyAppError = parseServerError(action.error);
      })
      .addCase(fetchDomainData.pending, (state, action) => {
        const silent = action.meta.arg?.silent === true;
        if (!silent) {
          state.fetchingDomainData = true;
        }
      })
      .addCase(fetchDomainData.fulfilled, (state, action) => {
        state.fetchingDomainData = false;
        state.fetchingDomainDataError = null;
        state.domainData = action.payload?.domains;
      })
      .addCase(fetchDomainData.rejected, (state, action) => {
        state.fetchingDomainData = false;
        const silent = action.meta.arg?.silent === true;
        if (!silent) {
          state.fetchingDomainDataError = parseServerError(action.error);
        }
      })
      .addCase(addDomain.pending, (state) => {
        state.fetchingAddDomain = true;
      })
      .addCase(addDomain.fulfilled, (state, action) => {
        state.fetchingAddDomain = false;
        state.fetchingAddDomainError = null;
        // Save domain_id to ssoFormData for use in subsequent steps
        if (action.payload?.domain_id) {
          state.ssoFormData.domain_id = action.payload.domain_id;
        }
      })
      .addCase(addDomain.rejected, (state, action) => {
        state.fetchingAddDomain = false;
        state.fetchingAddDomainError = action.payload;
      })
      .addCase(verificationDomain.pending, (state) => {
        state.fetchingvVrificationDomain = true;
      })
      .addCase(verificationDomain.fulfilled, (state, action) => {
        state.fetchingvVrificationDomain = false;
        state.fetchingvVrificationDomainError = null;
      })
      .addCase(verificationDomain.rejected, (state, action) => {
        state.fetchingvVrificationDomain = false;
        state.fetchingvVrificationDomainError = action.payload;
      })
      // New SSO reducers
      .addCase(setupSsoConfiguration.pending, (state) => {
        state.fetchingSetupSso = true;
      })
      .addCase(setupSsoConfiguration.fulfilled, (state, action) => {
        state.fetchingSetupSso = false;
        state.fetchingSetupSsoError = null;
        state.ssoConfiguration = action.payload;
        // Save sso_conf_id to ssoFormData for use in subsequent steps
        if (action.payload?.sso_conf_id) {
          state.ssoFormData.sso_conf_id = action.payload.sso_conf_id;
        }
      })
      		.addCase(setupSsoConfiguration.rejected, (state, action) => {
			state.fetchingSetupSso = false;
			state.fetchingSetupSsoError = action.payload || "Server error";
		})
      .addCase(addTestEmail.pending, (state) => {
        state.fetchingAddTestEmail = true;
      })
      .addCase(addTestEmail.fulfilled, (state, action) => {
        state.fetchingAddTestEmail = false;
        state.fetchingAddTestEmailError = null;
        // Store login_url if provided in response
        if (action.payload?.login_url) {
          state.loginUrl = action.payload.login_url;
        }
      })
      		.addCase(addTestEmail.rejected, (state, action) => {
			state.fetchingAddTestEmail = false;
			state.fetchingAddTestEmailError = action.payload || "Server error";
		})
      .addCase(getTestAuthUrl.pending, (state) => {
        state.fetchingTestAuthUrl = true;
      })
      .addCase(getTestAuthUrl.fulfilled, (state, action) => {
        state.fetchingTestAuthUrl = false;
        state.fetchingTestAuthUrlError = null;
        state.testAuthUrl = action.payload;
        // Store login_url if provided in response
        if (action.payload?.login_url) {
          state.loginUrl = action.payload.login_url;
        }
      })
      .addCase(getTestAuthUrl.rejected, (state, action) => {
        state.fetchingTestAuthUrl = false;
        state.fetchingTestAuthUrlError = parseServerError(action.error);
      })
      .addCase(getSsoConfStatus.pending, (state) => {
        state.fetchingSsoConfStatus = true;
      })
      .addCase(getSsoConfStatus.fulfilled, (state, action) => {
        state.fetchingSsoConfStatus = false;
        state.fetchingSsoConfStatusError = null;
        state.ssoConfStatus = action.payload;
      })
      .addCase(getSsoConfStatus.rejected, (state, action) => {
        state.fetchingSsoConfStatus = false;
        state.fetchingSsoConfStatusError = parseServerError(action.error);
      })
      .addCase(getSsoSettingsToAdd.pending, (state) => {
        state.fetchingSsoSettingsToAdd = true;
        state.fetchingSsoSettingsToAddError = null;
      })
      .addCase(getSsoSettingsToAdd.fulfilled, (state, action) => {
        state.fetchingSsoSettingsToAdd = false;
        state.fetchingSsoSettingsToAddError = null;
        state.ssoSettingsToAdd = action.payload;
      })
      .addCase(getSsoSettingsToAdd.rejected, (state, action) => {
        state.fetchingSsoSettingsToAdd = false;
        state.fetchingSsoSettingsToAddError = parseServerError(action.error);
      })
      .addCase(deactivateSso.pending, (state) => {
        state.deactivatingSso = true;
        state.deactivatingSsoError = null;
      })
      .addCase(deactivateSso.fulfilled, (state, action) => {
        state.deactivatingSso = false;
        state.deactivatingSsoError = null;
      })
      .addCase(deactivateSso.rejected, (state, action) => {
        state.deactivatingSso = false;
        state.deactivatingSsoError = parseServerError(action.error);
      })
      .addCase(activateSso.pending, (state) => {
        state.activatingSso = true;
        state.activatingSsoError = null;
      })
      .addCase(activateSso.fulfilled, (state, action) => {
        state.activatingSso = false;
        state.activatingSsoError = null;
      })
      .addCase(activateSso.rejected, (state, action) => {
        state.activatingSso = false;
        state.activatingSsoError = parseServerError(action.error);
      })
      .addCase(deleteSso.pending, (state) => {
        state.deletingSso = true;
        state.deletingSsoError = null;
      })
      .addCase(deleteSso.fulfilled, (state, action) => {
        state.deletingSso = false;
        state.deletingSsoError = null;
      })
      .addCase(deleteSso.rejected, (state, action) => {
        state.deletingSso = false;
        state.deletingSsoError = parseServerError(action.error);
      })
      .addCase(getAuthUrl.pending, (state) => {
        state.fetchingAuthUrl = true;
      })
      .addCase(getAuthUrl.fulfilled, (state, action) => {
        state.fetchingAuthUrl = false;
        state.fetchingAuthUrlError = null;
        state.authUrl = action.payload;
      })
      .addCase(getAuthUrl.rejected, (state, action) => {
        state.fetchingAuthUrl = false;
        state.fetchingAuthUrlError = parseServerError(action.error);
      });
  },
});
export const {
	clearFetchingAddDomainError,
	clearFetchingVerificationDomainError,
	clearFetchingSetupSsoError,
	clearFetchingAddTestEmailError,
	clearLoginUrl,
	setSsoConfStatusClientFailure,
	clearSsoConfStatus,
	updateSsoFormData,
	clearSsoFormData,
} = profileSlice.actions;
export default profileSlice.reducer;
